import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Shield, Mail } from 'lucide-react';

export const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/5" />
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6">Legal</Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Privacy <span className="text-gradient-cyan">Policy</span>
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
              Your privacy is important to us. Learn how we collect, use, and protect your information.
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            {/* Introduction */}
            <Card className="border-none shadow-lg mb-8">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold mb-4">Our Commitment to Your Privacy</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      Comal Glass Company is committed to safeguarding the privacy of our users. We want to assure you that we do not share your personal information with third parties. This privacy policy outlines how we collect, use, and protect the information you provide to us.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Information Collection */}
            <Card className="border-none shadow-lg mb-8">
              <CardContent className="pt-6">
                <h2 className="text-2xl font-bold mb-4">Information Collection</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We collect only the information necessary to provide and improve our services. This may include name, email address, phone number, and project details. We do not sell, rent, or share this information with any third parties.
                </p>
              </CardContent>
            </Card>

            {/* How We Use Your Information */}
            <Card className="border-none shadow-lg mb-8">
              <CardContent className="pt-6">
                <h2 className="text-2xl font-bold mb-4">How We Use Your Information</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  The information collected is used solely for communicating with the intended party. We may use the information provided to communicate with you via phone, text (SMS), and email. We do not share your information with external parties for marketing or any other purposes.
                </p>
                <div className="bg-primary/5 border-l-4 border-primary p-4 rounded">
                  <p className="text-sm text-foreground">
                    <strong>Note:</strong> Your information is stored securely and used exclusively for providing you with quotes, scheduling services, and customer support related to your glass installation needs.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Your Choices */}
            <Card className="border-none shadow-lg mb-8">
              <CardContent className="pt-6">
                <h2 className="text-2xl font-bold mb-4">Your Choices</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  You have the right to access, correct, or delete your information. If you have any concerns or questions about your data, please contact us at info@comalglass.com.
                </p>
                <div className="flex items-center space-x-3 p-4 bg-muted/30 rounded-lg">
                  <Mail className="h-5 w-5 text-primary flex-shrink-0" />
                  <div>
                    <p className="font-medium text-sm">Contact Us About Your Data</p>
                    <a 
                      href="mailto:info@comalglass.com" 
                      className="text-primary hover:underline text-sm"
                    >
                      info@comalglass.com
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Policy Changes */}
            <Card className="border-none shadow-lg mb-8">
              <CardContent className="pt-6">
                <h2 className="text-2xl font-bold mb-4">Policy Changes</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may update our privacy policy from time to time. Any changes will be communicated to you, and your continued use of our services implies your acceptance of the updated policy.
                </p>
              </CardContent>
            </Card>

            {/* Agreement */}
            <Card className="border-primary/20 bg-primary/5 shadow-lg">
              <CardContent className="pt-6">
                <p className="text-center text-muted-foreground leading-relaxed">
                  By using our services, you agree to the terms outlined in this privacy policy.
                </p>
                <p className="text-center text-sm text-muted-foreground mt-4">
                  <strong>Last Updated:</strong> December 2024
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Questions About Our <span className="text-gradient-cyan">Privacy Policy?</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              If you have any questions or concerns about how we handle your information, please don't hesitate to contact us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:info@comalglass.com"
                className="inline-flex items-center justify-center px-6 py-3 text-base font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <Mail className="mr-2 h-5 w-5" />
                Email Us
              </a>
              <a
                href="tel:8303599925"
                className="inline-flex items-center justify-center px-6 py-3 text-base font-medium rounded-lg border-2 border-primary text-primary hover:bg-primary/10 transition-colors"
              >
                Call (830) 359-9925
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
